import { startOfWeek, endOfWeek, format, addWeeks, subWeeks, startOfDay, endOfDay, subDays, addDays, startOfMonth, endOfMonth, addMonths, subMonths } from "date-fns";

/**
 * Get the start of the week (Monday) for a given date
 */
export function getWeekStart(date: Date): Date {
  return startOfWeek(date, { weekStartsOn: 1 }); // Monday
}

/**
 * Get the end of the week (Sunday) for a given date
 */
export function getWeekEnd(date: Date): Date {
  return endOfWeek(date, { weekStartsOn: 1 }); // Sunday
}

/**
 * Get the start of the rolling 7-day window (6 days ago at 00:00:00)
 */
export function getRollingWindowStart(date: Date): Date {
  const sixDaysAgo = subDays(date, 6);
  return startOfDay(sixDaysAgo);
}

/**
 * Get the end of the rolling 7-day window (today at 23:59:59)
 */
export function getRollingWindowEnd(date: Date): Date {
  return endOfDay(date);
}

/**
 * Get the date for "tomorrow" relative to a reference date
 */
export function getTomorrow(date: Date): Date {
  return addDays(date, 1);
}

/**
 * Get the date 8 days ago (blocks that will release tomorrow)
 */
export function getReleasingTomorrowDate(date: Date): Date {
  return subDays(date, 8);
}

/**
 * Format date as YYYY-MM-DD for Convex queries (UTC for consistency)
 * Use UTC on server, local on client to handle timezone properly
 */
export function formatDateForQuery(date: Date): string {
  // Use UTC date components for consistent server-side calculations
  // Client should convert local date to UTC date before passing to server
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
}

/**
 * Convert a local date selection to UTC date string
 * This handles the timezone conversion when user selects a date
 */
export function formatLocalDateAsUTC(date: Date): string {
  // Create a new Date object that represents the same calendar date in UTC
  // This ensures user selecting "Dec 17" gets stored as "Dec 17" UTC
  const utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  return formatDateForQuery(utcDate);
}

/**
 * Get week range as formatted strings
 */
export function getWeekRange(date: Date): { start: string; end: string } {
  return {
    start: formatDateForQuery(getWeekStart(date)),
    end: formatDateForQuery(getWeekEnd(date)),
  };
}

/**
 * Navigate to previous week
 */
export function getPreviousWeek(date: Date): Date {
  return subWeeks(date, 1);
}

/**
 * Navigate to next week
 */
export function getNextWeek(date: Date): Date {
  return addWeeks(date, 1);
}

/**
 * Format week range for display (e.g., "Dec 23 - Dec 29")
 */
export function formatWeekRange(start: Date, end: Date): string {
  return `${format(start, "MMM d")} - ${format(end, "MMM d")}`;
}

/**
 * Get the start of the month for a given date
 */
export function getMonthStart(date: Date): Date {
  return startOfMonth(date);
}

/**
 * Get the end of the month for a given date
 */
export function getMonthEnd(date: Date): Date {
  return endOfMonth(date);
}

/**
 * Navigate to previous month
 */
export function getPreviousMonth(date: Date): Date {
  return subMonths(date, 1);
}

/**
 * Navigate to next month
 */
export function getNextMonth(date: Date): Date {
  return addMonths(date, 1);
}

/**
 * Format month for display (e.g., "December 2024")
 */
export function formatMonth(date: Date): string {
  return format(date, "MMMM yyyy");
}

/**
 * Navigate to previous day
 */
export function getPreviousDay(date: Date): Date {
  return subDays(date, 1);
}

/**
 * Navigate to next day
 */
export function getNextDay(date: Date): Date {
  return addDays(date, 1);
}

/**
 * Format day for display (e.g., "Thu, Dec 26")
 */
export function formatDay(date: Date): string {
  return format(date, "EEE, MMM d");
}

/**
 * Check if a date is today
 */
export function isToday(date: Date): boolean {
  const today = new Date();
  return (
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate()
  );
}

/**
 * Convert a date string (YYYY-MM-DD) and time string (HH:MM) to a Unix timestamp.
 * Assumes local timezone.
 */
export function dateTimeToTimestamp(date: string, time: string): number {
  // Parse date parts
  const [year, month, day] = date.split("-").map(Number);
  // Parse time parts
  const [hours, minutes] = time.split(":").map(Number);

  // Create Date object in local timezone
  const d = new Date(year, month - 1, day, hours, minutes, 0, 0);
  return d.getTime();
}

/**
 * Calculate how many hours of a block fall within a time window.
 * Handles partial overlap at window boundaries.
 *
 * @param blockStart - Block start timestamp
 * @param blockEnd - Block end timestamp
 * @param windowStart - Window start timestamp
 * @param windowEnd - Window end timestamp (usually "now")
 * @returns Hours inside the window (0 if no overlap)
 */
export function calculateOverlapHours(
  blockStart: number,
  blockEnd: number,
  windowStart: number,
  windowEnd: number
): number {
  // If block is entirely outside window, no overlap
  if (blockEnd <= windowStart || blockStart >= windowEnd) {
    return 0;
  }

  // Calculate overlap region
  const overlapStart = Math.max(blockStart, windowStart);
  const overlapEnd = Math.min(blockEnd, windowEnd);

  // Convert ms to hours
  return (overlapEnd - overlapStart) / (1000 * 60 * 60);
}
